import React, { useState, useEffect } from 'react';
import './App.css';
import axios from 'axios';

import { CircleHelp } from 'lucide-react';


function NeuronView({ neuronData, isNegative }) {
  const [showPositive, setShowPositive] = useState(!isNegative);
  const [trueActivations, setTrueActivations] = useState(null);
  const [explanationIndex, setExplanationIndex] = useState(0);

  const [trueMultiplier, setTrueMultiplier] = useState(0);


  const [helpText, setHelpText] = useState('');
  const [showHelpPopup, setShowHelpPopup] = useState(false);
  const [helpPopupPosition, setHelpPopupPosition] = useState({ x: 0, y: 0 });

  const formatDescription = (description) => {
    return description.replace(/\{\{(.*?)\}\}/g, '<u>$1</u>');
  };

  const computeColor = (float) => {
    let MIN_COLOR = null;
    let MAX_COLOR = null;
    if (!showPositive) {
      MIN_COLOR = [248, 117, 87, 0];
      MAX_COLOR = [255, 0, 0, 1];
    } else {
      MAX_COLOR = [118, 135, 250, 0.96];
      MIN_COLOR = [174, 196, 245, 0];
    }
    return `rgba(${MIN_COLOR.map((c, i) => c + (MAX_COLOR[i] - c) * float).join(',')})`;
  }

  const handleQuestionMarkHover = (text, event) => {
    setHelpText(text);
    setShowHelpPopup(true);
    setHelpPopupPosition({ x: event.clientX, y: event.clientY });
  };

  const handleQuestionMarkLeave = () => {
    setShowHelpPopup(false);
  };


  useEffect(() => {
    if (!neuronData) return;
      let arr = [];
      for (let i = 0; i < neuronData.exemplar_tokens.length; i++) {
        arr.push(neuronData.exemplar_tokens[i][1]);
      }
      setTrueActivations(arr);
      setTrueMultiplier(1/Math.max(...arr.flat(Infinity)));
  }, [neuronData, showPositive])

  return (
    <>
      {trueActivations && (
        <div>
          <div style={{ fontSize: '16px', fontWeight: 500, margin: '10px' }}>
            Layer {neuronData['layer']}, Neuron {neuronData['neuron']}, {showPositive ? 'Positive' : 'Negative'} Activations
            <span
              style={{ marginLeft: '5px', cursor: 'help' }}
              onMouseEnter={(e) => handleQuestionMarkHover("The MLP neurons in Llama 3 can have positive or negative activations. Neurons' behaviors in the positive and negative ranges are qualitatively different, so we consider them separately.", e)}
              onMouseLeave={handleQuestionMarkLeave}
            >
              <CircleHelp style={{width: '16px', height: '16px'}}/>
            </span>
            <div style={{ fontSize: '12px', marginTop: '5px' }}>
            Check out our <a href="https://transluce.org/neuron-descriptions" target="_blank" rel="noopener noreferrer">writeup</a> to learn how we generated descriptions and scored them!
            </div>
          </div>
          <div style={{ width: '80%', marginLeft: 'auto', marginRight: 'auto'  }}>
            <div style={{ marginBottom: '20px', width: '100%' }}>
              <table style={{borderCollapse: 'collapse', textAlign: 'left', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontWeight: 500 }}>Description</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontWeight: 500 }}>
                      Score
                      <span
                        style={{ marginLeft: '5px', cursor: 'help' }}
                        onMouseEnter={(e) => handleQuestionMarkHover("Description score represents how well a simulator can predict the neuron's activations when given the description (Pearson correlation between simulated and true activations). A higher score indicates a better description.", e)}
                        onMouseLeave={handleQuestionMarkLeave}
                      >
                        <CircleHelp style={{width: '16px', height: '16px'}}/>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {neuronData.explanation_summary.map((explanation, index) => (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: explanationIndex === index ? '#f2f2f2' : 'white',
                        cursor: 'pointer'
                      }}
                      onClick={() => setExplanationIndex(index)}
                    >
                      <td style={{ border: '1px solid #ddd', padding: '8px' }} dangerouslySetInnerHTML={{ __html: formatDescription(explanation[0]) }}>
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{explanation[1].toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ fontWeight: 500, padding: '8px', border: '1px solid #ccc' }}>
                    True Activations
                    <span
                      style={{ marginLeft: '5px', cursor: 'help' }}
                      onMouseEnter={(e) => handleQuestionMarkHover("The neuron's true activations are its values over top-activating input sequences. Read more about how top-activating sequences are computed in our writeup.", e)}
                      onMouseLeave={handleQuestionMarkLeave}
                    >
                      <CircleHelp style={{width: '16px', height: '16px'}}/>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {neuronData.exemplar_tokens.map((exemplar, exemplarIndex) => (
                  <tr key={exemplarIndex}>
                    {[0].map((columnIndex) => (
                      <td key={columnIndex} style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'left' }}>
                        {exemplar[0].map((token, tokenIndex) => (
                          <span
                            key={tokenIndex}
                            style={{
                              marginRight: '0px',
                              backgroundColor: `${computeColor(trueMultiplier*Math.abs(trueActivations[exemplarIndex][tokenIndex]))}`
                            }}
                          >
                            {token}
                          </span>
                        ))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {showHelpPopup && (
              <div
                style={{
                  position: 'fixed',
                  top: helpPopupPosition.y + 20,
                  left: helpPopupPosition.x + 20,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  padding: '10px',
                  borderRadius: '5px',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                  zIndex: 1000,
                  maxWidth: '300px',
                }}
              >
                {helpText}
              </div>
            )}
          </div>
        </div>
        </div>
      )}
    </>
  );
}

export default NeuronView;
